<script setup lang="ts">
const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { field } = useInlineBlockEditor(props.id);

const buttonText = field<string>("buttonText", "Submit");
const icon = field<string>("icon", "lucide:send");
const iconPosition = field<string>("iconPosition", "left");
const size = field<string>("size", "md");
const alignment = field<string>("alignment", "center");
const variant = field<string>("variant", "default");
const automationPath = field<string>("automationPath", "");
const reportPath = field<string>("reportPath", "/reports/me");
const delaySeconds = field<number>("delaySeconds", 0);

const previewAlignmentClass = computed(() => {
  switch (alignment.value) {
    case "left":
      return "justify-start";
    case "right":
      return "justify-end";
    default:
      return "justify-center";
  }
});

const previewSize = computed(() => (["sm", "lg"].includes(size.value) ? size.value : "default"));
</script>

<template>
  <EditorSection :title="t('appearance')" icon="lucide:square-mouse-pointer" accent="assessment">
    <EditorField :label="t('buttonText')">
      <Input v-model="buttonText" class="h-8 rounded-sm bg-muted text-[13px]" />
    </EditorField>

    <div class="grid grid-cols-2 gap-2">
      <EditorField :label="t('icon')">
        <Input v-model="icon" class="h-8 rounded-sm bg-muted font-mono text-[13px]" placeholder="lucide:send" />
      </EditorField>
      <EditorField :label="t('iconPosition')">
        <Select :model-value="iconPosition" @update:model-value="(v) => (iconPosition = v as string)">
          <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="left">{{ t("left") }}</SelectItem>
            <SelectItem value="right">{{ t("right") }}</SelectItem>
          </SelectContent>
        </Select>
      </EditorField>
    </div>

    <div class="grid grid-cols-3 gap-2">
      <EditorField :label="t('size')">
        <Select :model-value="size" @update:model-value="(v) => (size = v as string)">
          <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="sm">{{ t("small") }}</SelectItem>
            <SelectItem value="md">{{ t("medium") }}</SelectItem>
            <SelectItem value="lg">{{ t("large") }}</SelectItem>
          </SelectContent>
        </Select>
      </EditorField>
      <EditorField :label="t('alignment')">
        <Select :model-value="alignment" @update:model-value="(v) => (alignment = v as string)">
          <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="left">{{ t("left") }}</SelectItem>
            <SelectItem value="center">{{ t("center") }}</SelectItem>
            <SelectItem value="right">{{ t("right") }}</SelectItem>
          </SelectContent>
        </Select>
      </EditorField>
      <EditorField :label="t('variant')">
        <Select :model-value="variant" @update:model-value="(v) => (variant = v as string)">
          <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Default</SelectItem>
            <SelectItem value="outline">Outline</SelectItem>
            <SelectItem value="secondary">Secondary</SelectItem>
            <SelectItem value="ghost">Ghost</SelectItem>
            <SelectItem value="destructive">Destructive</SelectItem>
            <SelectItem value="link">Link</SelectItem>
          </SelectContent>
        </Select>
      </EditorField>
    </div>

    <div class="rounded-sm border bg-muted/30 p-3">
      <p class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{{ t("preview") }}</p>
      <div class="flex w-full" :class="previewAlignmentClass">
        <Button :variant="(variant as any) || 'default'" :size="previewSize as any" type="button">
          <Icon mode="svg" v-if="icon && iconPosition !== 'right'" :name="icon" />
          <span>{{ buttonText || "Submit" }}</span>
          <Icon mode="svg" v-if="icon && iconPosition === 'right'" :name="icon" />
        </Button>
      </div>
    </div>
  </EditorSection>

  <EditorSection :title="t('behavior')" icon="lucide:workflow" accent="assessment" :default-open="false">
    <EditorField :label="t('automationPath')" :hint="t('automationHelp')">
      <EditorPathInput
        v-model="automationPath"
        mode="existing-file"
        :scopes="['site']"
        content-type-filter="automation"
        placeholder="/path/to/automation"
        aria-label="Automation path"
      />
    </EditorField>
    <EditorField :label="t('reportPath')" :hint="t('reportHelp')">
      <EditorPathInput
        v-model="reportPath"
        mode="existing-file"
        :scopes="['site', 'external']"
        allow-http-urls
        content-type-filter="report"
        placeholder="/reports/me"
        aria-label="Redirect path"
      />
    </EditorField>
    <EditorField :label="t('delaySeconds')" :hint="t('delayHelp')">
      <Input type="number" min="0" v-model.number="delaySeconds" class="h-8 rounded-sm bg-muted text-[13px]" />
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  appearance: Appearance
  behavior: Behavior
  buttonText: Button text
  icon: Icon (Iconify name)
  iconPosition: Icon position
  size: Size
  alignment: Alignment
  variant: Variant
  left: Left
  right: Right
  center: Center
  small: Small
  medium: Medium
  large: Large
  preview: Preview
  automationPath: Automation path
  automationHelp: Optional. The path of an automation to run when the form is valid.
  reportPath: Redirect path
  reportHelp: Path to navigate to after a successful submit.
  delaySeconds: Delay before redirect (seconds)
  delayHelp: Wait this long after the automation completes before redirecting.
</i18n>
